import React, { useState } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { API_BASE, API_ENDPOINTS, STORAGE_KEYS, USER_ROLES } from '../config'; // Added API_ENDPOINTS
import SettingsMenu from './SettingsMenu';
import './Dashboard.css'; // Reusing dashboard styles
import './Home.css'; // Reusing nav styles

const RouteDetailPage = () => {
  const { competitionId, routeId } = useParams();
  const { state } = useLocation();
  const { t, basePath } = useI18n();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Add menu state

  // User state for Header
  const username = localStorage.getItem(STORAGE_KEYS.username) || 'guest';
  const role = localStorage.getItem(STORAGE_KEYS.role) || USER_ROLES.user;
  const isLoggedIn = localStorage.getItem(STORAGE_KEYS.isLoggedIn) === 'true';
  const registryUrl = localStorage.getItem(STORAGE_KEYS.registryUrl) || '—';
  // 获取 token 用于视频鉴权
  const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);

  const roleLabel = role === USER_ROLES.siteAdmin
    ? '系统管理员'
    : role === USER_ROLES.competitionAdmin
      ? '赛事管理员'
      : '普通用户';

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.isLoggedIn);
    localStorage.removeItem(STORAGE_KEYS.username);
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.registryUrl);
    localStorage.removeItem(STORAGE_KEYS.role);
    navigate(`${basePath}/leaderboard`);
  };

  const closeMenu = () => setIsMenuOpen(false); // Add close helper

  const record = state?.record;
  const jsonFile = state?.jsonFile || 'unknown_submission'; // 从 state 获取 identifiers
  const videoType = 'rgb_front'; // 默认视角，可根据需求扩展

  // Video URL construction using new API endpoints
  const getFullUrl = (relativePath) => {
    let url = relativePath;
    if (!relativePath.startsWith('http')) {
      url = `${API_BASE}${relativePath}`;
    }
    // Append token as query parameter to handle authentication for static resources/media
    // 修复 401 错误：将 token 拼接到 URL 参数中
    if (accessToken) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}token=${accessToken}`;
    }
    return url;
  };

  const videoUrl = getFullUrl(API_ENDPOINTS.videoStream(competitionId, routeId, jsonFile, videoType));
  const downloadUrl = getFullUrl(API_ENDPOINTS.videoDownload(competitionId, routeId, jsonFile, videoType));

  const handleBack = () => {
    if (competitionId) {
      // Navigate back to the competition page, specifically the dashboard tab
      navigate(`${basePath}/leaderboard/competitions/${competitionId}`, { state: { activeTab: 'dashboard' } });
    } else {
      navigate(-1);
    }
  };

  if (!record) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <h2>{t('dashboard.details.notFound') || 'Record Details Not Found'}</h2>
        <p style={{ color: 'var(--muted)', marginBottom: '20px' }}>{t('dashboard.details.missingState') || 'Please access this page from the dashboard.'}</p>
        <button onClick={handleBack} className="primary-btn">{t('action.back') || 'Go Back'}</button>
      </div>
    );
  }
  
  return (
    <div className="route-detail-page">
      <nav className="navbar">
        <div className="nav-brand">
          <img className="nav-logo" src="/asynscale_ai_logo.png" alt="AsynScale AI Logo" />
          <span className="nav-title">Bench2Drive Leaderboard</span>
        </div>
        
        <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? '✕' : '☰'}
        </button>
        <div className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
          <Link to={`${basePath}/leaderboard`} className="nav-link" onClick={closeMenu}>{t('nav.home')}</Link>
          <Link to={`${basePath}/leaderboard/competitions`} className="nav-link" onClick={closeMenu}>{t('nav.competitions')}</Link>
          <button 
             className="nav-link" 
             style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.95rem', fontFamily: 'inherit' }}
             onClick={() => { navigate(`${basePath}/leaderboard/competitions/${competitionId}`, { state: { activeTab: 'leaderboard' } }); closeMenu(); }}
          >
            {t('competition.leaderboard')}
          </button>
          <button 
             className="nav-link active" 
             style={{ border: 'none', cursor: 'pointer', fontSize: '0.95rem', fontFamily: 'inherit' }}
             onClick={() => { navigate(`${basePath}/leaderboard/competitions/${competitionId}`, { state: { activeTab: 'dashboard' } }); closeMenu(); }}
          >
            {t('competition.myData')}
          </button>
          <SettingsMenu />
          {!isLoggedIn ? (
            <Link to={`${basePath}/leaderboard/login`} className="nav-link nav-login" onClick={closeMenu}>{t('nav.login')}</Link>
          ) : (
            <div className="user-menu">
              <div className="user-menu-trigger">👤 {username}</div>
              <div className="user-menu-panel">
                <div className="user-menu-row">
                  <span className="user-menu-label">用户名</span>
                  <span>{username}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">角色</span>
                  <span>{roleLabel}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">Registry</span>
                  <span>{registryUrl}</span>
                </div>
                <button className="user-menu-logout" onClick={() => { handleLogout(); closeMenu(); }}>退出登录</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="dashboard-container">
        {/* 使用 dashboard-content 类来替代内联 padding，确保响应式一致性 */}
        <div className="dashboard-content" style={{ paddingTop: '30px' }}>
          
          {/* Breadcrumb / Title Header */}
          <div className="route-header-wrapper">
            <div>
              <button onClick={handleBack} className="ghost-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingLeft: '14px', paddingRight: '14px' }}>
                 ← {t('action.back')}
              </button>
              <div className="route-title-group">
                <h1 className="dashboard-title" style={{ fontSize: '2rem', marginBottom: '0', wordBreak: 'break-all' }}>{routeId}</h1>
                <span className={`record-status ${record.status.includes('Failed') ? 'failed' : 'success'}`} style={{ fontSize: '1rem', padding: '6px 12px' }}>
                  {record.status}
                </span>
              </div>
            </div>
          </div>

          {/* Layout: Video (Larger) & Metrics */}
          <div className="route-detail-layout">
            
            {/* Main Content Area: Video */}
            <div className="video-section">
               {showWarning && (
                  <div style={{ 
                    background: '#fbbf24', 
                    color: '#000', 
                    padding: '12px 20px', 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: '12px 12px 0 0'
                  }}>
                    <span>{t('dashboard.video.warning')}</span>
                    <button 
                      onClick={() => setShowWarning(false)}
                      style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#000', padding: '0 5px' }}
                    >
                      ✕
                    </button>
                  </div>
               )}
              <div className="video-player-container" style={{ borderRadius: showWarning ? '0 0 12px 12px' : '12px' }}>
                <video 
                  controls 
                  style={{ width: '100%', aspectRatio: '16/9', display: 'block' }}
                  src={videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
               <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                   <a href={downloadUrl} download target="_blank" rel="noreferrer" className="primary-btn" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', fontSize: '1rem' }}>
                     {t('dashboard.video.download')}
                   </a>
              </div>
            </div>

            {/* Sidebar: Metrics & Infractions */}
            <div className="metrics-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Scores Card */}
              <div className="score-card primary" style={{ padding: '30px', textAlign: 'left' }}>
                 <h3 style={{ marginTop: 0, color: 'var(--muted)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('dashboard.score.total')}</h3>
                 <div style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--text)', fontFamily: 'SF Mono, monospace' }}>
                   {record.scores.score_composed?.toFixed(3)}
                 </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                <div className="score-card" style={{ padding: '20px', borderRadius: '12px', background: 'var(--surface)', border: '1px solid var(--border)' }}>
                   <span style={{ display: 'block', color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '8px' }}>{t('dashboard.score.route')}</span>
                   <strong style={{ fontSize: '1.5rem', fontFamily: 'SF Mono, monospace' }}>
                     {record.scores.score_route?.toFixed(2)}%
                   </strong>
                </div>
                <div className="score-card" style={{ padding: '20px', borderRadius: '12px', background: 'var(--surface)', border: '1px solid var(--border)' }}>
                   <span style={{ display: 'block', color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '8px' }}>{t('dashboard.score.penalty')}</span>
                   <strong style={{ fontSize: '1.5rem', fontFamily: 'SF Mono, monospace' }}>{record.scores.score_penalty?.toFixed(3)}</strong>
                </div>
              </div>

              {/* Infractions List */}
              {record.infractions && (
                <div className="exceptions-section" style={{ marginTop: '0' }}>
                   <h3 style={{ marginTop: 0, marginBottom: '15px' }}>{record.num_infractions} {t('dashboard.infractions')}</h3>
                   <ul className="exception-list">
                      {Object.entries(record.infractions).map(([key, val]) => {
                         // Skip empty arrays or zero values to keep UI clean
                         if (Array.isArray(val) && val.length === 0) return null;
                         if (typeof val === 'number' && val === 0) return null;

                         return (
                           <li key={key} className="exception-item">
                             <span className="exception-route" style={{ color: 'var(--warn-text)' }}>
                               {key} <span style={{ color: 'var(--muted)', fontSize: '0.9em' }}>
                                {Array.isArray(val) ? `(${val.length})` : ''}
                               </span>
                             </span>
                             <span className="exception-msg" style={{color: 'var(--text)', fontSize: '0.9rem'}}>
                               {Array.isArray(val) ? (
                                 <ul style={{ paddingLeft: '20px', margin: '5px 0 0' }}>
                                   {val.map((v, i) => <li key={i}>{v}</li>)}
                                 </ul>
                               ) : val}
                             </span>
                           </li>
                         );
                      })}
                   </ul>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
      
      {/* Mobile Responsive Style Injection */}
      <style>{`
        .route-detail-layout {
          display: grid;
          grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
          gap: 30px;
          align-items: start;
        }
        .route-header-wrapper {
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          margin-bottom: 30px;
        }
        .route-title-group {
          display: flex; 
          align-items: baseline; 
          gap: 15px;
          flex-wrap: wrap;
        }
        .video-player-container {
          background: #000;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(0,0,0,0.5);
          border: 1px solid var(--border);
        }

        @media (max-width: 900px) {
          .route-detail-layout {
            grid-template-columns: 1fr;
          }
          .route-title-group {
            flex-direction: column;
            gap: 8px;
          }
          .video-section {
            order: -1; /* Ensure video stays on top */
          }
        }

        @media (max-width: 768px) {
           .dashboard-content {
             padding: 0 16px 40px !important;
           }
        }
      `}</style>
    </div>
  );
};

export default RouteDetailPage;